import { useContext, useEffect, useMemo, useRef } from 'react';
import type { ElementType, ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { MAX_SURFACE_DEPTH, type SurfaceRung } from '@/tokens/colors';
import { nextRung, scopeToCssVars, surfaceScopes } from '@/tokens/surfaceScopes';
import { SurfaceContext, type SurfaceContextValue } from './surfaceContext';

export interface SurfaceProps extends Omit<BoxProps, 'component'> {
  /**
   * Force a rung instead of deriving one from the parent.
   *
   * Needed for portalled content, which cannot inherit through the DOM. Prefer
   * letting the rung derive: an explicit value is a claim about where this
   * element sits that the component tree can no longer keep honest.
   */
  rung?: SurfaceRung;
  /** Render a different element. Defaults to `div`. */
  component?: ElementType;
  children?: ReactNode;
}

/**
 * A background that knows where it is.
 *
 * Wrapping content in a `Surface` does two things: it paints the correct rung
 * for its nesting depth, and it declares the `--ds-*` custom properties that
 * descendants read to stay consistent with it — an input recesses one rung
 * below, quiet text picks a tier that still clears contrast, a border picks a
 * value that is legible against this specific background.
 *
 * Nesting is capped at three levels (`MAX_SURFACE_DEPTH`). A fourth is painted
 * at its parent's rung rather than continuing up the ladder, and logs an error
 * in development. The cap exists because the rungs are 2–3 L\* apart: past three
 * steps the steps stop reading as hierarchy and start reading as noise, and the
 * text ramp runs out of contrast headroom. If you need a fourth level, the
 * answer is almost always that one of the outer three should not be a surface.
 */
export function Surface({ rung: explicitRung, sx, style, children, ...rest }: SurfaceProps) {
  const parent = useContext(SurfaceContext);
  const mode = useTheme().palette.mode;

  const depth = parent ? parent.depth + 1 : 0;
  const exceedsCeiling = depth > MAX_SURFACE_DEPTH;

  const rung: SurfaceRung = explicitRung
    ? explicitRung
    : !parent
      ? 'page'
      : exceedsCeiling
        ? // Clamp rather than saturate at `overlay`: repainting at the parent's
          // rung makes the nesting visually invisible, which is a truer signal
          // that the level is not carrying its weight.
          parent.rung
        : nextRung(parent.rung);

  const warned = useRef(false);
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !exceedsCeiling || warned.current) return;
    warned.current = true;
    console.error(
      `[octopus-ui] Surface nested ${depth} levels deep, past the ceiling of ${MAX_SURFACE_DEPTH}. ` +
        `Painted at "${rung}" (its parent's rung) instead of stepping up, so this level is ` +
        `invisible. Flatten the hierarchy: one of the enclosing surfaces is probably a plain ` +
        `container rather than a surface. See the Surface Ladder docs.`,
    );
  }, [depth, exceedsCeiling, rung]);

  const value = useMemo<SurfaceContextValue>(() => ({ depth, rung }), [depth, rung]);
  const scope = surfaceScopes[mode][rung];

  return (
    <SurfaceContext.Provider value={value}>
      <Box
        data-surface={rung}
        data-surface-depth={depth}
        // Hex values in `sx` get concatenated into Emotion class names; a `#`
        // there is parsed as an ID selector and the whole rule is dropped.
        style={{ ...scopeToCssVars(scope), ...style }}
        sx={[
          {
            backgroundColor: 'var(--ds-bg)',
            color: 'var(--ds-text)',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {children}
      </Box>
    </SurfaceContext.Provider>
  );
}
